/**
 * Scheduled rotation for the homepage Discovery panel: five batches a day
 * (morning / midday / afternoon / evening / night), each a stable set of
 * three products that holds for the whole period. The periods are the
 * greeting module's — timeGroupFor is imported, not reimplemented — so the
 * greeting and the row of products below it always change together.
 *
 * This replaces the click-based exclusion store. Opening a card is plain
 * navigation now — nothing here records a click, and nothing retires a
 * product because it was looked at. The only thing stored is "these ids
 * were *displayed*, at this time", so the next batch can ask for
 * something else.
 *
 * The record is per browser: there is no account-level impression store
 * on the backend (RailExposure is global per product+rail, and
 * AnalyticsEvent is deliberately not used for rotation state), so a
 * second device starts its own history.
 */
import { timeGroupFor } from "../components/home/discoveryGreetings";

export const ROTATION_VERSION = 1;
export const STORAGE_PREFIX = "hr_discovery_rotation.v1";
export const BATCH_SIZE = 3;
export const HISTORY_DAYS = 7;
// The backend's own validated ceiling on ?exclude= (backend/app/main.py,
// _parse_exclude_param). A longer list is trimmed, never sent. Five periods
// a day x 7 days x BATCH_SIZE 3 is at most 105 ids, so a full week of
// history still fits with room to spare.
export const MAX_EXCLUDE = 200;

/** Per-account storage key; guests are kept in their own namespace so two
 *  people sharing a browser never inherit each other's rotation. */
export const rotationKeyFor = (accountId) =>
  `${STORAGE_PREFIX}:${accountId == null || accountId === "" ? "guest" : accountId}`;

const pad = (n) => String(n).padStart(2, "0");
const dateStamp = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

/** The day a period belongs to. Night runs 21:00–04:59, so 02:00 on the
 *  14th is still the night batch that started on the 13th — the shopper
 *  experiences one continuous night, not two. This has to name whichever
 *  group timeGroupFor() gives to the small hours; it was `evening` when the
 *  day had three periods and is `night` now that it has five. */
export function batchDayFor(date = new Date()) {
  const group = timeGroupFor(date);
  const day = new Date(date.getTime());
  if (group === "night" && day.getHours() < 5) day.setDate(day.getDate() - 1);
  return dateStamp(day);
}

/** `"2026-09-13:night"` — the identity of one batch. */
export function batchKeyFor(date = new Date()) {
  return `${batchDayFor(date)}:${timeGroupFor(date)}`;
}

export const dayOfBatchKey = (batchKey) => String(batchKey ?? "").split(":")[0];

/** Unique, ascending, positive integers — the shape the request and the
 *  query key both use, so a filtered response can never be served from an
 *  entry built for a different set. */
export function normalizeIds(ids) {
  const out = new Set();
  for (const raw of Array.isArray(ids) ? ids : []) {
    const n = typeof raw === "number" ? raw : Number.parseInt(raw, 10);
    if (Number.isInteger(n) && n > 0) out.add(n);
  }
  return [...out].sort((a, b) => a - b);
}

const emptyStore = () => ({ version: ROTATION_VERSION, batches: {}, history: [] });

const validBatchKey = (key) =>
  /^\d{4}-\d{2}-\d{2}:(morning|midday|afternoon|evening|night)$/.test(key);

/** Anything unreadable, corrupt, or from another version reads as "no
 *  history" rather than throwing — a blocked or full localStorage must
 *  degrade to "always a fresh batch", never to a broken panel. */
export function readStore(accountId) {
  let raw = null;
  try {
    raw = localStorage.getItem(rotationKeyFor(accountId));
  } catch {
    return emptyStore();
  }
  if (!raw) return emptyStore();
  let parsed;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return emptyStore();
  }
  if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return emptyStore();
  if (parsed.version !== ROTATION_VERSION) return emptyStore();

  const batches = {};
  const source = parsed.batches && typeof parsed.batches === "object" ? parsed.batches : {};
  for (const [key, entry] of Object.entries(source)) {
    if (!validBatchKey(key) || !entry || typeof entry !== "object") continue;
    const ids = normalizeIds(entry.ids);
    if (!ids.length) continue;
    batches[key] = { ids, at: Number.isFinite(entry.at) ? entry.at : 0 };
  }
  const history = [];
  for (const entry of Array.isArray(parsed.history) ? parsed.history : []) {
    if (!entry || typeof entry !== "object") continue;
    const [id] = normalizeIds([entry.id]);
    if (!id) continue;
    history.push({ id, at: Number.isFinite(entry.at) ? entry.at : 0, day: typeof entry.day === "string" ? entry.day : "" });
  }
  return { version: ROTATION_VERSION, batches, history };
}

/** Writes the store, dropping everything older than the history window —
 *  except the batch being written and the one the panel is showing right
 *  now, which survive however odd their timestamp looks (a device whose
 *  clock jumped must not have the row pulled out from under it). */
function writeStore(accountId, store, { now = Date.now(), keep = null } = {}) {
  const cutoff = now - HISTORY_DAYS * 24 * 60 * 60 * 1000;
  const current = batchKeyFor(new Date(now));
  const batches = {};
  for (const [key, entry] of Object.entries(store.batches)) {
    if (key === keep || key === current || entry.at >= cutoff) batches[key] = entry;
  }
  const history = store.history.filter((h) => h.at >= cutoff);
  const next = { version: ROTATION_VERSION, batches, history };
  try {
    localStorage.setItem(rotationKeyFor(accountId), JSON.stringify(next));
  } catch {
    /* storage blocked or full: this session simply keeps no history */
  }
  return next;
}

/** The ids fixed for this period, or null if it hasn't been drawn yet. */
export function readBatch(accountId, batchKey) {
  const entry = readStore(accountId).batches[batchKey];
  return entry ? entry.ids : null;
}

/**
 * Fixes `ids` as this period's batch and records them as displayed.
 *
 * Two tabs can open at once and draw different sets; whichever was
 * written first wins, so both settle on the same three products instead
 * of each keeping its own. Already-stored batch → that one is returned
 * and nothing is overwritten.
 */
export function saveBatch(accountId, batchKey, ids, { now = Date.now() } = {}) {
  if (!validBatchKey(batchKey)) return normalizeIds(ids);
  const store = readStore(accountId);
  const existing = store.batches[batchKey];
  const mine = { ids: normalizeIds(ids), at: now };
  if (!mine.ids.length) return existing ? existing.ids : [];
  if (existing && earlierWins(existing, mine) === existing) return existing.ids;

  store.batches[batchKey] = mine;
  const day = dayOfBatchKey(batchKey);
  const seen = new Set(store.history.filter((h) => h.day === day).map((h) => h.id));
  for (const id of mine.ids) {
    if (!seen.has(id)) store.history.push({ id, at: now, day });
  }
  writeStore(accountId, store, { now, keep: batchKey });
  return mine.ids;
}

/** Earliest write wins; identical timestamps fall back to a stable
 *  comparison of the ids so two tabs can't flip each other forever. */
function earlierWins(a, b) {
  if (a.at !== b.at) return a.at < b.at ? a : b;
  return a.ids.join(",") <= b.ids.join(",") ? a : b;
}

/**
 * Called when another tab writes the same key. Returns the ids this tab
 * should now show — usually the other tab's, since it wrote first.
 * Re-writes only when this tab's batch is the earlier one, which
 * terminates: each round strictly favours the earlier entry.
 */
export function reconcileBatch(accountId, batchKey, mineIds, { now = Date.now() } = {}) {
  const mine = normalizeIds(mineIds);
  const stored = readStore(accountId).batches[batchKey];
  // Nothing stored yet (this tab drew first, or the write was lost): claim
  // it, still through saveBatch so the earliest write keeps winning.
  if (!stored) return mine.length ? saveBatch(accountId, batchKey, mine, { now }) : [];
  // Otherwise the stored batch is the shared answer. saveBatch already
  // resolved which draw was first, so adopting it can't bounce back.
  return stored.ids;
}

/** ids displayed within the history window, most recently shown first. */
export function recentlyShownIds(accountId, { now = Date.now(), days = HISTORY_DAYS } = {}) {
  const cutoff = now - days * 24 * 60 * 60 * 1000;
  const seen = new Set();
  const out = [];
  for (const entry of [...readStore(accountId).history].sort((a, b) => b.at - a.at)) {
    if (entry.at < cutoff || seen.has(entry.id)) continue;
    seen.add(entry.id);
    out.push(entry.id);
  }
  return out;
}

/** ids displayed in today's batches — the day a period belongs to, so an
 *  after-midnight evening still counts as the same day. */
export function shownTodayIds(accountId, { now = Date.now() } = {}) {
  const day = batchDayFor(new Date(now));
  return [...new Set(readStore(accountId).history.filter((h) => h.day === day).map((h) => h.id))]
    .sort((a, b) => a - b);
}

/**
 * What to send as `?exclude=`, in the order to try it.
 *
 * A week of history is the preference; if the backend can't fill three
 * around it, the same day alone is the rule that actually matters, and an
 * unfiltered request is the last resort so the panel is never empty
 * merely because the catalogue is small. Bounded to three stages — the
 * caller steps forward on a short response, never loops.
 */
export function excludeStages(accountId, { now = Date.now() } = {}) {
  const week = recentlyShownIds(accountId, { now }).slice(0, MAX_EXCLUDE);
  const today = shownTodayIds(accountId, { now }).slice(0, MAX_EXCLUDE);
  const stages = [];
  for (const stage of [normalizeIds(week), normalizeIds(today), []]) {
    const key = stage.join(",");
    if (stages.some((s) => s.join(",") === key)) continue;   // no duplicate request
    stages.push(stage);
  }
  return stages;
}

/** Signed-in for the first time in this browser: the guest's rotation
 *  history carries over, so the account doesn't immediately re-show what
 *  the same person just saw. Guest history is then cleared. */
export function adoptGuestRotation(accountId, { now = Date.now() } = {}) {
  if (accountId == null) return [];
  const guest = readStore(null);
  if (!guest.history.length && !Object.keys(guest.batches).length) return [];
  const store = readStore(accountId);
  const known = new Set(store.history.map((h) => `${h.day}:${h.id}`));
  const adopted = [];
  for (const entry of guest.history) {
    if (known.has(`${entry.day}:${entry.id}`)) continue;
    store.history.push(entry);
    adopted.push(entry.id);
  }
  for (const [key, entry] of Object.entries(guest.batches)) {
    if (!store.batches[key]) store.batches[key] = entry;
  }
  writeStore(accountId, store, { now, keep: batchKeyFor(new Date(now)) });
  try {
    localStorage.removeItem(rotationKeyFor(null));
  } catch {
    /* nothing to clean up if storage is unavailable */
  }
  return normalizeIds(adopted);
}
